import { useTimezone } from '@app/core/hooks/useTimezone';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  ListItemText,
  Paper,
  Popper,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { NullState } from '@ui/components';
import moment from 'moment-timezone';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Options } from 'uplot';
import Chart from '../../../graph/LineGraph';
import { pastelColors } from '../../transcriptions/utils';
import {
  stationsEnum,
  StatusYoutubeMetadataByCountry,
  TimePoint,
  transformDataForUplot,
} from '../helpers';

interface YoutubeStatsViewProps {
  data: TimePoint[];
  isLoading: boolean;
  stationsSelection: string;
  setStationsSelection: (stationsSelection: string) => void;
  stationsList: { channelId: string; channelName: string }[];
  selectedStations: string[];
  setSelectedStations: (selectedStations: string[]) => void;
  dateRange: { label: string; range: { from: string; to: string } };
  dateRanges: { label: string; range: { from: string; to: string } }[];
  handleDateRangeChange: (selectedLabel: string) => void;
  statusYoutubeMetadataByCountry: StatusYoutubeMetadataByCountry[];
}
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const YoutubeStatsView = memo((props: YoutubeStatsViewProps) => {
  const {
    data,
    isLoading,
    dateRange,
    dateRanges,
    handleDateRangeChange,
    stationsSelection,
    setStationsSelection,
    stationsList,
    selectedStations,
    setSelectedStations,
    statusYoutubeMetadataByCountry,
  } = props;

  const { formatMessage } = useIntl();
  const tz = useTimezone();
  const transformedData = useMemo(() => {
    return data
      ? transformDataForUplot(data, statusYoutubeMetadataByCountry, tz, pastelColors)
      : { data: [[], []] as any, series: [] };
  }, [data, statusYoutubeMetadataByCountry, tz]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [divWidth, setDivWidth] = useState(0);
  const [selectValue, setSelectValue] = useState(selectedStations);
  const [isPopperOpen, setIsPopperOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      setDivWidth(containerRef.current.offsetWidth);
    }
  }, []);

  const opts: Options = {
    width: divWidth,
    height: 640,
    series: [
      {}, // this empty object is required, otherwise the lines will not be displayed
      ...transformedData.series,
    ],
    legend: {
      markers: {
        fill: (u, seriesIdx) => {
          const stroke = u.series[seriesIdx]?.stroke;
          if (typeof stroke === 'function') {
            const result = stroke(u, seriesIdx);
            if (typeof result === 'string') {
              return result;
            }
          }
          return 'transparent';
        },
      },
    },
    axes: [
      {
        space: 40,
        incrs: [1, 5, 10, 15, 30, 60, 60 * 2, 60 * 3, 60 * 6, 60 * 12, 60 * 24, 60 * 48],
        values: (u, vals, space) => {
          return vals.map((v) => {
            const momentTime = moment.unix(v).tz(tz);
            return momentTime.format('HH:mm');
          });
        },
      },
      {
        space: 40,
        size: 60,
      },
    ],
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: '1rem',
        margin: '0 0 1rem 0',
        justifyContent: 'center',
        flexDirection: 'column',
      }}
      ref={containerRef}
    >
      <div
        style={{
          alignSelf: 'space-between',
          display: 'flex',
          justifyContent: 'space-between',
          margin: '0 2rem',
          alignItems: 'baseline',
        }}
      >
        <ToggleButtonGroup
          value={stationsSelection}
          exclusive
          onChange={(_, value) => setStationsSelection(value)}
        >
          <ToggleButton value={stationsEnum.current}>
            <FormattedMessage
              id="logger.streamScreen.youtubeStats.currentStation"
              defaultMessage="Emisora actual"
            />
          </ToggleButton>
          <ToggleButton value={stationsEnum.all}>
            <FormattedMessage
              id="logger.streamScreen.youtubeStats.allStations"
              defaultMessage="Todos los canales"
            />
          </ToggleButton>
          <ToggleButton value={stationsEnum.selection}>
            <FormattedMessage
              id="logger.streamScreen.youtubeStats.selectStations"
              defaultMessage="Seleccionar canales"
            />
          </ToggleButton>
        </ToggleButtonGroup>

        {stationsSelection === stationsEnum.selection && (
          <div style={{ margin: '8px', width: 300 }}>
            <Button
              ref={anchorRef}
              variant="outlined"
              onClick={() => setIsPopperOpen(!isPopperOpen)}
              sx={{
                width: '100%',
                justifyContent: 'flex-start',
                textAlign: 'left',
                textTransform: 'none',
                padding: '10px 14px',
                fontSize: '1rem',
                fontFamily: 'inherit',
                position: 'relative',
                backgroundColor: 'transparent',
                color: 'rgba(0, 0, 0, 0.87)',
                borderColor: 'rgba(0, 0, 0, 0.23)',
                '&:hover': {
                  borderColor: '#92e1fe',
                  backgroundColor: 'transparent',
                },
                '&:focus': {
                  borderColor: '#1cc0fc',
                  borderWidth: '2px',
                  backgroundColor: 'transparent',
                },
                '&:active': {
                  backgroundColor: 'transparent',
                },
                '&.Mui-focusVisible': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <span
                style={{
                  display: 'block',
                  width: 'calc(100% - 20px)',
                  fontSize: '16px',
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {selectValue.length === 0 ? (
                  <em style={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                    <FormattedMessage id="common.select" defaultMessage="Seleccionar" />
                  </em>
                ) : (
                  selectValue
                    .map(
                      (id: string) =>
                        stationsList.find((station) => station.channelId === id)?.channelName,
                    )
                    .join(', ')
                )}
              </span>
              <span
                style={{
                  position: 'absolute',
                  right: '5px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: 'rgba(0, 0, 0, 0.54)',
                }}
              >
                <ArrowDropDownIcon />
              </span>
            </Button>

            <Popper
              open={isPopperOpen}
              anchorEl={anchorRef.current}
              placement="bottom-start"
              style={{ width: 300, zIndex: 1300 }}
            >
              <ClickAwayListener onClickAway={() => setIsPopperOpen(false)}>
                <Paper
                  sx={{
                    maxHeight: ITEM_HEIGHT * 5.5 + ITEM_PADDING_TOP,
                    width: '100%',
                    overflow: 'auto',
                  }}
                >
                  {stationsList.map((station) => (
                    <div
                      key={station.channelId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 16px',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        const newValue = selectValue.includes(station.channelId)
                          ? selectValue.filter((id) => id !== station.channelId)
                          : [...selectValue, station.channelId];
                        setSelectValue(newValue);
                      }}
                    >
                      <Checkbox checked={selectValue.includes(station.channelId)} />
                      <ListItemText primary={station.channelName} />
                    </div>
                  ))}

                  <Paper
                    sx={{
                      position: 'sticky',
                      bottom: 0,
                      zIndex: 1,
                      padding: 1,
                      display: 'flex',
                      justifyContent: 'space-around',
                      backgroundColor: 'background.paper',
                      boxShadow: '0 -2px 5px rgba(0,0,0,0.1)',
                    }}
                  >
                    <Button
                      onClick={() => {
                        setSelectValue([]);
                      }}
                    >
                      <FormattedMessage id="common.text.clear" defaultMessage="Borrar" />
                    </Button>
                    <Button
                      onClick={() => {
                        setSelectedStations(selectValue);
                        setIsPopperOpen(false);
                      }}
                    >
                      <FormattedMessage id="common.text.filter" defaultMessage="Aplicar" />
                    </Button>
                  </Paper>
                </Paper>
              </ClickAwayListener>
            </Popper>
          </div>
        )}
        <ToggleButtonGroup
          value={dateRange.label}
          exclusive
          onChange={(_, value) => value && handleDateRangeChange(value)}
        >
          <ToggleButton value={dateRanges[0].label}>
            1 <FormattedMessage id="logger.streamScreen.youtubeStats.hour" defaultMessage="Hora" />
          </ToggleButton>
          <ToggleButton value={dateRanges[1].label}>
            3{' '}
            <FormattedMessage id="logger.streamScreen.youtubeStats.hours" defaultMessage="Horas" />
          </ToggleButton>
          <ToggleButton value={dateRanges[2].label}>
            6{' '}
            <FormattedMessage id="logger.streamScreen.youtubeStats.hours" defaultMessage="Horas" />
          </ToggleButton>
          <ToggleButton value={dateRanges[3].label}>
            12{' '}
            <FormattedMessage id="logger.streamScreen.youtubeStats.hours" defaultMessage="Horas" />
          </ToggleButton>
          <ToggleButton value={dateRanges[4].label}>
            24{' '}
            <FormattedMessage id="logger.streamScreen.youtubeStats.hours" defaultMessage="Horas" />
          </ToggleButton>
        </ToggleButtonGroup>
      </div>
      {isLoading ? (
        <Box>
          <Skeleton variant="rectangular" width="100%" height={640} />
        </Box>
      ) : !isLoading && transformedData.data.length > 0 ? (
        <Chart options={opts} data={transformedData.data} width="" height="" timezone={tz} />
      ) : (
        <NullState className="p-7">
          {formatMessage({
            id: 'logger.youtubeStats.noDataForPeriod',
            defaultMessage: 'No hay datos disponibles para este período',
          })}
        </NullState>
      )}
    </div>
  );
});

export default YoutubeStatsView;
